/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { createContext, type ForwardedRef, forwardRef } from 'react';
import { composeRenderProps, type ContextValue, Provider, Toolbar as ToolbarCore, type ToolbarProps as ToolbarCoreProps, useContextProps } from 'react-aria-components';
import { breakpoints } from '../../constants.stylex';
import { AvatarContext } from '../avatar/avatar.component';
import { CloseSidebarButtonContext } from '../close-sidebar-button/close-sidebar-button.component';
import { FilterListContext } from '../filter-list/filter-list.component';
import { HeadingContext } from '../heading/heading.component';
import { IconButtonContext } from '../icon-button/icon-button.component';
import { SearchFieldContext } from '../search-field/search-field.component';
import { SegmentedButtonGroupContext } from '../segmented-button-group/segmented-button-group.component';
import { SelectStyleContext } from '../select/select.component';
import { ViewContext } from '../view/view.component';

interface TableToolbarProps extends Omit<ToolbarCoreProps, 'className' | 'style'> {
  style?: stylex.StyleXStyles;
}

const TableToolbarContext = createContext<ContextValue<TableToolbarProps, HTMLDivElement>>({});

const TableToolbar = forwardRef((props: TableToolbarProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TableToolbarContext);

  const { style, ...coreProps } = props;

  return (
    <ToolbarCore ref={ref} {...stylex.props(styles.base, style)} {...coreProps}>
      {composeRenderProps(props.children, (children) => (
        <Provider values={[
          [ViewContext, {
            slots: {
              [TABLE_TOOLBAR_SLOTS.LEFT_VIEW]: {
                style: viewStyles.left,
              },
              [TABLE_TOOLBAR_SLOTS.RIGHT_VIEW]: {
                style: viewStyles.right,
              }
            }
          }],
          [HeadingContext, {
            level: 1,
            size: 20,
            style: headingStyles.base
          }],
          [SegmentedButtonGroupContext, {
            disallowEmptySelection: true,
            style: segmentedButtonGroupStyles.base
          }],
          [SearchFieldContext, {
            size: 36,
            isRounded: true,
            style: searchBarStyles.base,
          }],
          [CloseSidebarButtonContext, {
            isRounded: false,
            style: closeSidebarIconButtonStyles.base,
          }],
          [IconButtonContext, {
            variant: 'ghost',
            size: 40,
          }],
          [AvatarContext, {
            size: 32,
          }],
          [SelectStyleContext, {
            size: 32,
            style: selectStyles.base
          }],
          [FilterListContext, {
            style: filterListStyles.base
          }],
        ]}>
          {children}
        </Provider>))}
    </ToolbarCore>
  );
});

const styles = stylex.create({
  base: {
    gap: {
      default: 0,
      [breakpoints.small]: 8,
    },
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: {
      default: 'row',
      [breakpoints.small]: 'column',
    },
    height: {
      default: 56,
      [breakpoints.small]: 'fit-content',
    },
    paddingBottom: 12,
    paddingLeft: 20,
    paddingRight: 12,
    paddingTop: {
      default: 12,
      [breakpoints.small]: 8,
    },
    width: '100%',
  },
});

const headingStyles = stylex.create({
  base: {
    marginRight: {
      default: null,
      [breakpoints.small]: 'auto',
    },
  },
});

const viewStyles = stylex.create({
  left: {
    gap: {
      default: 16,
      [breakpoints.small]: 0,
    },
    alignItems: 'center',
    display: 'flex',
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    minHeight: {
      default: null,
      [breakpoints.medium]: 40,
      [breakpoints.small]: 40,
    },
    minWidth: 0,
    width: {
      default: null,
      [breakpoints.small]: '100%',
    },
  },
  right: {
    alignItems: 'center',
    display: {
      default: 'flex',
      ':empty': 'none',
    },
    justifyContent: 'flex-end',
    width: {
      default: null,
      [breakpoints.small]: '100%',
    }
  }
});

const closeSidebarIconButtonStyles = stylex.create({
  base: {
    marginLeft: -8,
    marginRight: -8,
  }
});

const segmentedButtonGroupStyles = stylex.create({
  base: {
    display: {
      default: 'inline-flex',
      [breakpoints.small]: 'grid',
    },
    gridAutoColumns: {
      default: null,
      [breakpoints.small]: '1fr',
    },
    gridAutoFlow: {
      default: null,
      [breakpoints.small]: 'column',
    },
    height: {
      default: 'auto',
      [breakpoints.small]: '100%',
    },
    width: {
      default: 'auto',
      [breakpoints.small]: '100%',
    },
  }
});

const searchBarStyles = stylex.create({
  base: {
    maxWidth: 262,
    minWidth: 262,
  }
});

const selectStyles = stylex.create({
  base: {
    width: 200
  }
});

const filterListStyles = stylex.create({
  base: {
    flexShrink: 1,
    minWidth: 0,
  }
});

const TABLE_TOOLBAR_SLOTS = {
  RIGHT_VIEW: 'right-view',
  LEFT_VIEW: 'left-view',
};

export { TABLE_TOOLBAR_SLOTS, TableToolbar, TableToolbarContext, type TableToolbarProps };
